import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import { isNil } from 'mojito/utils';
import MojitoServices from 'mojito/services';
import SelectionButton from 'modules/selection-button/index.js';
import SelectionsContext from 'modules/selection-button/context/selections-context.js';
import PrebuiltBetSelectionsContext from 'modules/prebuilt-bets/prebuilt-bet/context/prebuilt-bet-selections-context.js';
import ShowAllSelectionsButton from 'modules/aggregated-market-pane/show-all-selections-button';
import MatchAccaSelectionItemAvantgarde from 'modules/match-acca/match-acca-selection-item/avantgarde/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import {
    useLimitedParts,
    usePrebuiltBetInfo,
    usePartsMetadata,
} from 'modules/prebuilt-bets/prebuilt-bet/hooks/index.js';

import { useMemo, memo } from 'react';
import { useSelector } from 'react-redux';

const { FlexPane, Button, Text } = MojitoPresentation.Components;
const { PRE_BUILT_BET } = MojitoServices.Betslip.types.BETTING_CONTEXT;
const { selectLanguage } = MojitoCore.Services.SystemSettings.selectors;
const { NumberUtils } = MojitoCore.Base;
const EventUtils = MojitoServices.SportsContent.Events.utils;
const EMPTY_OBJECT = {};

// eslint-disable-next-line react/display-name
const PrebuiltBetAvantgarde = memo(props => {
    const {
        mojitoTools: { config, l10n, stringResolver },
        event,
        market,
        boostedMarket,
        onEventClick,
        showMarketName,
        shouldRequestData,
        children,
        class: className,
    } = props;

    const { limitedParts, minimized, showAllButtonVisible, onToggleShowAllButton } =
        useLimitedParts(market?.preBuiltBetBuilderParts, config.partsThreshold);

    const selectionsContext = useMemo(
        () => new PrebuiltBetSelectionsContext(market, boostedMarket, event),
        [market, boostedMarket, event]
    );

    const betInfo = usePrebuiltBetInfo(market, boostedMarket, l10n);
    const { enabled: showBetCount, minimumBetCount } = config.betCount;
    // Bet count is only on the prebuilt market's selection, not the boosted one.
    const betCount = EventUtils.getPrebuiltBetCount(market?.selections?.[0], minimumBetCount);

    return (
        <FlexPane
            class={`ta-PrebuiltBet ${className}`.trim()}
            config={config.genericEventCard.container}
        >
            <Button
                class="ta-clickableArea"
                onClick={onEventClick}
                disabled={!config.showCardHeader || !config.cardClickable}
                config={config.genericEventCard.clickableArea}
            >
                {children}
                <FlexPane config={config.matchAccaSelectionsContainer}>
                    <MatchAccaSelections
                        showLastChildSeparator={minimized && showAllButtonVisible}
                        parts={limitedParts}
                        showMarketName={showMarketName}
                        shouldRequestData={shouldRequestData}
                        l10n={l10n}
                        config={config.matchAccaSelectionItem}
                    />
                </FlexPane>
            </Button>
            {showAllButtonVisible && (
                <ShowAllSelectionsButton
                    config={config.showAllSelectionsButton}
                    minimized={minimized}
                    onMinimizeChange={onToggleShowAllButton}
                />
            )}
            <SelectionsContext.Provider value={selectionsContext}>
                <FlexPane
                    config={config.footerContainer}
                    class="ta-PrebuiltBetFooter"
                    testId="ta-PrebuiltBetFooter"
                >
                    {showBetCount && !isNil(betCount) && (
                        <BetCount
                            betCount={betCount}
                            config={config}
                            stringResolver={stringResolver}
                        />
                    )}
                    <CallToAction config={config} event={event} betInfo={betInfo} />
                </FlexPane>
            </SelectionsContext.Provider>
        </FlexPane>
    );
});

const MatchAccaSelections = ({
    parts,
    showMarketName,
    shouldRequestData,
    l10n,
    config,
    showLastChildSeparator,
}) => {
    const showPartMetadata = Boolean(config.showPartMetadata);
    const partsMetadata = usePartsMetadata(parts, showPartMetadata, l10n, shouldRequestData);

    return (parts || []).map((part, i, row) => {
        const metadata = partsMetadata[part.id] || EMPTY_OBJECT;
        return (
            <MatchAccaSelectionItemAvantgarde
                config={config}
                key={`${part.id}`}
                showSeparator={showLastChildSeparator || i + 1 !== row.length}
                selectionLabel={BetslipModuleUtils.resolveNameAndHandicapLabel(part, l10n)}
                marketName={showMarketName ? part.marketName : undefined}
                eventName={part.eventName}
                earlyPayout={showPartMetadata ? metadata.earlyPayout : part.earlyPayout}
                eventStartTime={metadata.eventStartTime}
                price={metadata.price}
                basePrice={metadata.basePrice}
                neutral={metadata.neutral}
            />
        );
    });
};

const CallToAction = ({ config, event, betInfo }) => {
    const isBoosted = Boolean(betInfo.basePrice);

    return (
        <FlexPane config={config.selectionContentContainer}>
            <SelectionButton
                config={isBoosted ? config.boostedSelectionButton : config.selectionButton}
                selection={betInfo.selection}
                event={event}
                marketStatus={betInfo.market?.status}
                marketType={betInfo.market?.type}
                marketId={betInfo.market?.id}
                basePrice={betInfo.basePrice}
                bettingContext={PRE_BUILT_BET}
            />
        </FlexPane>
    );
};

const BetCount = ({ betCount, config, stringResolver }) => {
    const locale = useSelector(selectLanguage);

    return (
        <FlexPane config={config.betCountContainer} class="ta-betCount" testId="ta-betCount">
            <FlexPane
                config={config.betCountValueContainer}
                class="ta-betCountValueContainer"
                testId="ta-betCountValueContainer"
            >
                <Text
                    config={config.betCountValue}
                    class="ta-betCountValue"
                    testId="ta-betCountValue"
                >
                    {NumberUtils.formatCompactNumber(betCount, locale)}
                </Text>
            </FlexPane>
            <Text config={config.betCountLabel} class="ta-betCountLabel" testId="ta-betCountLabel">
                {stringResolver.resolveString('$PRE_BUILT_BET.BETS_PLACED_LABEL')}
            </Text>
        </FlexPane>
    );
};

export default PrebuiltBetAvantgarde;
